/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';

import { Text } from '@/shared/ui';

import { StackedField } from './sale-detail-atoms';
import type { Sale } from '../types/sale.types';

export interface SaleOutcomeCardProps {
  sale: Sale;
}

/**
 * How the sale ended, shown in place of the action row.
 *
 * Both terminal states are final by design: undoing a concluded sale is a new
 * business act, not a rewind of this record. The card says so plainly rather
 * than leaving a reader hunting for the buttons that used to be there.
 *
 * @param props.sale - The terminal sale record.
 * @returns The rendered outcome card.
 */
export function SaleOutcomeCard({ sale }: SaleOutcomeCardProps) {
  const { t } = useTranslation('features/sales');
  const voided = sale.state === 'voided';
  const accent = voided ? 'var(--accent-cobre)' : 'var(--accent-salvia)';

  return (
    <div
      data-testid="sale-outcome-card"
      className="flex flex-col gap-(--space-2-5) rounded-(--radius-lg) px-(--space-4) py-(--space-4)"
      style={{
        border: `1px solid color-mix(in srgb, ${accent} 40%, var(--border-subtle))`,
        background: `color-mix(in srgb, ${accent} 8%, var(--surface-primary))`,
      }}
    >
      <div className="flex items-center gap-(--space-2)">
        <div
          className="size-2 shrink-0 rounded-(--radius-full)"
          style={{ background: accent }}
          aria-hidden="true"
        />
        <Text component="span" variant="heading" tone="primary">
          {voided ? t('detail.outcome.voidedTitle') : t('detail.outcome.completedTitle')}
        </Text>
      </div>

      <Text component="span" variant="bodySm" tone="secondary" className="leading-relaxed">
        {voided ? t('detail.outcome.voidedBody') : t('detail.outcome.completedBody')}
      </Text>

      {voided && sale.cancellation_reason && (
        <StackedField
          label={t('detail.outcome.reasonLabel')}
          value={sale.cancellation_reason}
        />
      )}

      <Text component="span" variant="meta" tone="tertiary">
        {t('detail.readOnly')}
      </Text>
    </div>
  );
}

export default SaleOutcomeCard;
